import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { usePoll } from "../hooks/usePoll";
import { useTableQuery } from "../hooks/useTableQuery";
import { SearchInput, Th, Pager } from "../components/Table";
import { GrowthCharts } from "../components/GrowthCharts";
import { ActivationFunnel, type FunnelStage } from "../components/ActivationFunnel";
import { InfoHint } from "../components/Tooltip";

// The consolidated overview (feedback #8) — the one dashboard for the soft
// launch. Two cumulative growth charts on top (users, memexes), then the org
// table below: searchable + per-column sortable + paginated, each row carrying a
// "chart" checkbox. Unchecking an org excludes it from the charts above, so the
// operator can watch the whole platform, any combination of orgs, or drill into
// one. Click an org name to open its page.
interface OrgRow {
  id: string;
  name: string;
  handle: string | null;
  createdAt: string;
  userCount: number;
  memexCount: number;
  specCount: number;
  isInternal: boolean;
  isPersonal: boolean;
}

export function OverviewPage() {
  const tq = useTableQuery("createdAt", "desc");
  // Defaults: hide internal (Mindset's own orgs) ON, include personal OFF — the
  // board opens on real external org signal. Both are operator-toggleable.
  const [hideInternal, setHideInternal] = useState(true);
  const [includePersonal, setIncludePersonal] = useState(false);
  // Orgs the operator has UNCHECKED — excluded from the charts. Default empty =
  // every org contributes. Tracked by id so it survives pagination.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const fetcher = useCallback(() => {
    const p = new URLSearchParams(tq.params);
    if (hideInternal) p.set("hideInternal", "true");
    p.set("includePersonal", includePersonal ? "true" : "false");
    return api<{ orgs: OrgRow[]; total: number }>(`/api/orgs?${p}`);
  }, [tq.params, hideInternal, includePersonal]);
  const { data, error } = usePoll(fetcher);
  const orgs = data?.orgs ?? [];

  // The platform-wide activation funnel (spec-13) — population drop-off across the
  // org activation journey. Polls the aggregate rollup; the per-org strip still
  // lives on the org page.
  const funnelFetcher = useCallback(
    () => api<{ funnel: FunnelStage[] }>("/api/stats/activation-funnel"),
    [],
  );
  const { data: funnelData, error: funnelError } = usePoll(funnelFetcher);

  const chartQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (excluded.size) p.set("exclude", [...excluded].join(","));
    if (hideInternal) p.set("hideInternal", "true");
    p.set("includePersonal", includePersonal ? "true" : "false");
    return p.toString();
  }, [excluded, hideInternal, includePersonal]);

  const chartScope =
    (includePersonal
      ? "Charts: whole platform (every org + personal memexes)"
      : "Charts: all orgs") + (excluded.size ? `, minus ${excluded.size} org${excluded.size === 1 ? "" : "s"}` : "");

  const toggleOrg = useCallback((id: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-8 py-8">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl text-heading">Overview</h1>
          <p className="text-sm text-muted">
            Live platform growth and per-org stats, refreshing every 10s.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label
            className="flex items-center gap-2 text-sm text-secondary"
            title="Show personal (org-less) memexes — where solo adoption often starts — in the charts and as their own rows in the table below."
          >
            <input
              type="checkbox"
              checked={includePersonal}
              onChange={(e) => setIncludePersonal(e.target.checked)}
              data-testid="include-personal"
            />
            Include personal
          </label>
          <label
            className="flex items-center gap-2 text-sm text-secondary"
            title="Hide orgs marked internal (Mindset's own) from the table and the charts, to show real external signal."
          >
            <input
              type="checkbox"
              checked={hideInternal}
              onChange={(e) => setHideInternal(e.target.checked)}
              data-testid="hide-internal"
            />
            Hide internal
          </label>
        </div>
      </header>

      <div className="mb-3">
        <GrowthCharts query={chartQuery} />
        <p className="mt-2 text-xs text-muted" data-testid="chart-scope">
          {chartScope}
        </p>
      </div>

      <section className="mt-8 rounded-xl border border-edge bg-surface p-4" data-testid="activation-funnel-card">
        <h2 className="mb-1 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
          Activation funnel
          <InfoHint text="How many orgs have reached each stage of the activation journey, across the whole platform. The biggest step-down is where to intervene. Per-org detail lives on each org's page." />
        </h2>
        <p className="mb-3 text-xs text-muted">Platform-wide — every org, all time.</p>
        {funnelError ? (
          <p className="text-sm text-status-danger-text">Failed to load the activation funnel.</p>
        ) : !funnelData ? (
          <p className="text-sm text-muted" data-testid="funnel-loading">Loading…</p>
        ) : (
          <ActivationFunnel stages={funnelData.funnel} />
        )}
      </section>

      <section className="mt-8">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wider text-muted">Organizations</h2>
          <SearchInput value={tq.search} onChange={tq.setSearch} placeholder="Search name or handle…" />
        </div>

        {error && <p className="text-sm text-status-danger-text">Failed to load orgs.</p>}

        <div className="overflow-hidden rounded-xl border border-edge" data-testid="org-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-edge">
                <th
                  className="w-12 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted"
                  title="Tick to include this org's data in the charts above; untick to exclude it."
                >
                  <span className="inline-flex items-center gap-1">
                    Chart
                    <InfoHint text="Tick to include this org's data in the charts above; untick to exclude it. Personal memexes follow the 'Include personal' switch." align="start" />
                  </span>
                </th>
                <Th label="Name" field="name" sort={tq.sort} order={tq.order} onSort={tq.toggleSort} hint="Org name, or a personal memex when 'Include personal' is on. Click to open it." />
                <Th label="Users" field="userCount" sort={tq.sort} order={tq.order} onSort={tq.toggleSort} align="right" hint="Active members of the org (personal-memex rows show 1)." />
                <Th label="Memexes" field="memexCount" sort={tq.sort} order={tq.order} onSort={tq.toggleSort} align="right" hint="Memexes in the org's namespace (personal rows are a single memex, so 1)." />
                <Th label="Specs" field="specCount" sort={tq.sort} order={tq.order} onSort={tq.toggleSort} align="right" hint="Specs across the org's memexes (non-archived)." />
                <Th label="Created" field="createdAt" sort={tq.sort} order={tq.order} onSort={tq.toggleSort} align="right" hint="When the org (or personal memex) was created." />
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} className="border-b border-edge-subtle last:border-0 hover:bg-card-hover">
                  <td className="px-4 py-3">
                    {o.isPersonal ? (
                      // Personal memexes are toggled together by the "Include
                      // personal" switch, so the per-row box just reflects it.
                      <input
                        type="checkbox"
                        checked={includePersonal}
                        disabled
                        title="Personal memexes are toggled together by 'Include personal' above."
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={!excluded.has(o.id)}
                        onChange={() => toggleOrg(o.id)}
                        data-testid={`chart-toggle-${o.id}`}
                        aria-label={`Include ${o.name} in charts`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {o.isPersonal ? (
                      <Link to={`/memexes/${o.id}`} className="font-medium text-primary hover:text-accent" data-testid="personal-row-link">
                        {o.name}
                      </Link>
                    ) : (
                      <Link to={`/orgs/${o.id}`} className="font-medium text-primary hover:text-accent" data-testid="org-row-link">
                        {o.name}
                      </Link>
                    )}
                    {o.isInternal && (
                      <span className="ml-2 rounded-full border border-status-neutral-border bg-status-neutral-bg px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-status-neutral-text">
                        internal
                      </span>
                    )}
                    {o.isPersonal && (
                      <span className="ml-2 rounded-full border border-status-info-border bg-status-info-bg px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-status-info-text">
                        personal
                      </span>
                    )}
                    <div className="text-xs text-muted">{o.handle ? `@${o.handle}` : "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-secondary">{o.userCount}</td>
                  <td className="px-4 py-3 text-right text-secondary">{o.memexCount}</td>
                  <td className="px-4 py-3 text-right text-secondary">{o.specCount}</td>
                  <td className="px-4 py-3 text-right text-muted">{new Date(o.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={tq.page} pageSize={tq.pageSize} total={data?.total ?? 0} onPage={tq.setPage} />
      </section>
    </div>
  );
}
